import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Link2, FileText, Search } from "lucide-react";

interface CaptureKnowledgeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CaptureKnowledgeDialog({ open, onOpenChange }: CaptureKnowledgeDialogProps) {
  const [url, setUrl] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [researchQuery, setResearchQuery] = useState("");

  const handleUrlCapture = () => {
    console.log('Capturing URL:', url);
    onOpenChange(false);
  };

  const handleNoteCapture = () => {
    console.log('Saving note:', noteContent);
    onOpenChange(false);
  };

  const handleResearchCapture = () => {
    console.log('Starting research:', researchQuery);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" data-testid="dialog-capture-knowledge">
        <DialogHeader>
          <DialogTitle>Capture Knowledge</DialogTitle>
          <DialogDescription>
            Add new information to your knowledge base
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="url" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="url" data-testid="tab-url">
              <Link2 className="h-4 w-4 mr-2" />
              URL
            </TabsTrigger>
            <TabsTrigger value="note" data-testid="tab-note">
              <FileText className="h-4 w-4 mr-2" />
              Note
            </TabsTrigger>
            <TabsTrigger value="research" data-testid="tab-research">
              <Search className="h-4 w-4 mr-2" />
              Research
            </TabsTrigger>
          </TabsList>
          <TabsContent value="url" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="url">Article or webpage URL</Label>
              <Input
                id="url"
                placeholder="https://example.com/article"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="input-url"
              />
            </div>
            <Button onClick={handleUrlCapture} className="w-full" data-testid="button-capture-url">
              Capture & Analyze
            </Button>
          </TabsContent>
          <TabsContent value="note" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="note">Your note</Label>
              <Textarea
                id="note"
                placeholder="Write your thoughts, ideas, or observations..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                className="min-h-[200px]"
                data-testid="textarea-note"
              />
            </div>
            <Button onClick={handleNoteCapture} className="w-full" data-testid="button-save-note">
              Save Note
            </Button>
          </TabsContent>
          <TabsContent value="research" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="research">Research query</Label>
              <Textarea
                id="research"
                placeholder="What do you want to learn about?"
                value={researchQuery}
                onChange={(e) => setResearchQuery(e.target.value)}
                className="min-h-[150px]"
                data-testid="textarea-research"
              />
            </div>
            <Button onClick={handleResearchCapture} className="w-full" data-testid="button-start-research">
              Start AI Research
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
